import * as Yup from 'yup';
import { InferType } from 'yup';
import { useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { RHFCheckbox, FormProvider, RHFTextField, useAuthContext } from '@emfood/shared';

import { LoadingButton } from '@mui/lab';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  Stack,
  Button,
  TextField,
  Typography,
  DialogTitle,
  Autocomplete,
  DialogActions,
  DialogContent,
} from '@mui/material';

import { useGetBuyerSites, useAddFavouritesCollection } from 'src/api/hooks/api-hooks';

interface CreateSharedFavouritesCollectionFormProps {
  onBack: () => void;
}

export default function CreateSharedFavouritesCollectionForm({
  onBack,
}: CreateSharedFavouritesCollectionFormProps) {
  const { user } = useAuthContext();

  const { mutate: addFavouritesCollection, isLoading: isCreatingCollection } =
    useAddFavouritesCollection();
  const { data: buyerSites = [] } = useGetBuyerSites(user?.buyerOrg?.id ?? null);
  const queryClient = useQueryClient();

  const AddSharedFavouritesListSchema = Yup.object().shape({
    name: Yup.string()
      .max(100, 'Favourites list name must be shorter than 100 characters')
      .required('List Name is required'),
    buyerSites: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string().required(),
          name: Yup.string().required(),
        })
      )
      .test(
        'sites-or-org-required',
        'Please select at least one buyer site or assign to entire organisation',
        (value, context) => {
          const { assignToOrg } = context.parent;
          return assignToOrg || (value && value.length > 0);
        }
      ),
    assignToOrg: Yup.boolean(),
  });

  const defaultValues = useMemo(
    () => ({
      name: '',
      buyerSites: [],
      assignToOrg: false,
    }),
    []
  );

  type ValidationSchemaType = InferType<typeof AddSharedFavouritesListSchema>;

  const methods = useForm<ValidationSchemaType>({
    resolver: yupResolver(AddSharedFavouritesListSchema),
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting, errors },
    reset,
  } = methods;

  const assignToOrg = watch('assignToOrg');

  const onSubmit = handleSubmit(async (data: ValidationSchemaType) => {
    addFavouritesCollection(
      {
        body: {
          name: data.name,
          ...(data.assignToOrg
            ? { buyerOrgId: user?.buyerOrg?.id }
            : { buyerSiteIds: data.buyerSites?.map((site) => site.id) }),
        },
      },
      {
        onSuccess: () => {
          enqueueSnackbar('Shared Favourites List Created Successfully', { variant: 'success' });
          queryClient.invalidateQueries(['favourites']);
          reset();
          onBack();
        },
      }
    );
  });

  const handleBack = () => {
    reset();
    onBack();
  };

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <DialogTitle>
        <Typography variant="h2">Create New Shared Favourites List</Typography>
        <Typography variant="subtitle2">
          Create a list and assign sites or share with entire organisation
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <RHFTextField name="name" label="List Name" />

          <Controller
            name="buyerSites"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                multiple
                options={buyerSites}
                disablePortal
                getOptionLabel={(option) => option.name}
                onChange={(_, newValue) => field.onChange(newValue)}
                disabled={assignToOrg}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buyer Sites"
                    placeholder="Select sites"
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                ChipProps={{
                  deleteIcon: <CancelIcon />,
                }}
              />
            )}
          />

          <RHFCheckbox name="assignToOrg" label="Assign to entire organisation" />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleBack} variant="outlined">
          Back
        </Button>
        <LoadingButton
          type="submit"
          variant="contained"
          loading={isSubmitting || isCreatingCollection}
        >
          Create List
        </LoadingButton>
      </DialogActions>
    </FormProvider>
  );
}
